import {tokens} from '@commercial-helios/ui/theme'
import React from 'react'
import styled from 'styled-components'

import type {CookieDetailsFragment, LegalPageCookiePolicyQuery} from '@/graphql-types'
import Button from '@helios/shared/components/button'
import checkIconGreen from '@helios/shared/images/icons/check-green.svg'
import {Section} from '@helios/shared/styles/grid'
import {NavTextLink} from '@helios/shared/styles/typography'
import type {PrismicData} from '@helios/shared/types/prismic-data'
import mq from '@helios/shared/utils/media-query'

import {CookieCategory} from '../cookie-category'

interface Props {
    content: PrismicData<LegalPageCookiePolicyQuery, 'allPrismicCookieManager'>['data']
}

type CookieCategoryKeys = keyof PrismicData<
    LegalPageCookiePolicyQuery,
    'allPrismicCookieManager'
>['data']['cookie_categories'][0]

type ToggleState = Record<CookieCategoryKeys, boolean>

const ACCEPT_ALL_STATE = {
    ACCEPTED: 'accepted',
    REJECTED: 'rejected',
    INITIAL: 'initial',
} as const

type AcceptAllKeys = keyof typeof ACCEPT_ALL_STATE
type AcceptAllValues = typeof ACCEPT_ALL_STATE[AcceptAllKeys]

const isNecessaryCookie = (cookie: CookieCategoryKeys) => cookie === 'necessary_cookies'

const toggleAllCookies = (cookieCategories: CookieCategoryKeys[], state: boolean) =>
    cookieCategories.reduce<ToggleState>((acc, curr) => {
        acc[curr] = state ? true : isNecessaryCookie(curr) ? true : false
        return acc
    }, {} as ToggleState)

export const CookieManager = ({content}: Props) => {
    const cookieCategories = Object.keys(
        content.cookie_categories[0]
    ) as unknown as CookieCategoryKeys[]

    const initialToggleState = cookieCategories.reduce<ToggleState>((acc, curr) => {
        acc[curr] = isNecessaryCookie(curr) ? true : false
        return acc
    }, {} as ToggleState)

    const [toggleState, setToggleState] = React.useState<ToggleState>(initialToggleState)
    const [showSuccessMessage, setShowSuccessMessage] = React.useState<AcceptAllValues>(
        ACCEPT_ALL_STATE.INITIAL
    )

    const cookieCategoryContent = Object.entries(content.cookie_categories[0]).filter(
        ([_, cookieContent]) => {
            return cookieContent.document
        }
    ) as Array<[CookieCategoryKeys, {document: CookieDetailsFragment}]>

    const acceptAll = () => {
        setToggleState(toggleAllCookies(cookieCategories, true))
        setShowSuccessMessage(ACCEPT_ALL_STATE.ACCEPTED)
    }

    const declineAll = () => {
        setToggleState(toggleAllCookies(cookieCategories, false))
        setShowSuccessMessage(ACCEPT_ALL_STATE.REJECTED)
    }

    return (
        <Section>
            {cookieCategoryContent.map(([id, cookieContent]) => (
                <CookieCategory
                    id={id}
                    content={cookieContent.document.data}
                    toggleState={toggleState[id]}
                    onToggle={() =>
                        setToggleState({
                            ...toggleState,
                            [id]: !toggleState[id],
                        })
                    }
                    headings={{
                        name: content.cookie_name_heading,
                        duration: content.cookie_duration_heading,
                        origin: content.cookie_origin_heading,
                        purpose: content.cookie_purpose_heading,
                    }}
                />
            ))}

            <Buttons>
                <StyledNavTextLink as="button" onClick={declineAll}>
                    {content.decline_cookies_text}
                </StyledNavTextLink>
                <StyledButton onClick={acceptAll}>{content.accept_cookies_text}</StyledButton>
            </Buttons>
            {showSuccessMessage !== ACCEPT_ALL_STATE.INITIAL && (
                <SuccessMessage role="alert">
                    {showSuccessMessage === ACCEPT_ALL_STATE.ACCEPTED
                        ? content.accept_all_cookies_success_message
                        : content.decline_all_cookies_success_message}
                </SuccessMessage>
            )}
        </Section>
    )
}

const mobileMenuBreakpoint = 'sm'

const Buttons = styled.div`
    display: grid;
    grid-gap: ${tokens.spacing16};
    justify-content: stretch;
    text-align: center;

    ${mq.min(mobileMenuBreakpoint)} {
        padding-bottom: ${tokens.spacing8};
        justify-content: center;
        grid-auto-flow: column;
        grid-gap: ${tokens.spacing32};
    }
`

const StyledNavTextLink = styled(NavTextLink)`
    width: 100%;
    text-decoration: none;

    ${mq.min(mobileMenuBreakpoint)} {
        justify-self: end;
    }
`

const StyledButton = styled(Button)`
    ${mq.min(mobileMenuBreakpoint)} {
        justify-self: start;
    }
`

const SuccessMessage = styled.p`
    margin-top: ${tokens.spacing16};
    color: ${tokens.green900};
    text-align: center;

    :before {
        margin-right: ${tokens.spacing12};
        content: url(${checkIconGreen});
    }

    ${mq.min(mobileMenuBreakpoint)} {
        margin-top: ${tokens.spacing24};
    }
`
